import { cn } from "@/lib/utils";

/**
 * Agent avatar square — the one visual identity every agent carries in the
 * redesigned sidebar, headers and insights tables.
 *
 * Uses `avatarUrl` when the agent has one; otherwise two-letter initials on a
 * tint chosen deterministically from the agent id, so the same agent is the
 * same colour everywhere without storing anything.
 */

type Tint = { light: [string, string]; dark: [string, string] };

// bg / initials, light → dark. Neutral is reserved for the idle stack.
const TINTS: Tint[] = [
  { light: ["#d7efe6", "#0f5c46"], dark: ["#1f4a3d", "#c9f2e3"] },
  { light: ["#e7e0f6", "#4b3778"], dark: ["#3b3252", "#e4dcf7"] },
  { light: ["#dbe8f0", "#2c4a5d"], dark: ["#2f3f4a", "#d9ecf5"] },
  { light: ["#f2e4d2", "#5b3f22"], dark: ["#4a3a2a", "#f5e3cf"] },
  { light: ["#dce5f7", "#2b4471"], dark: ["#2a3a5a", "#d4e2fb"] },
  { light: ["#f6dfe3", "#6b2d3a"], dark: ["#4a2a31", "#f7d6dd"] },
  { light: ["#e2eedb", "#3b5a2a"], dark: ["#33452a", "#dcefd0"] },
];
export const NEUTRAL_TINT: Tint = { light: ["#ececea", "#6b6c72"], dark: ["#2a2b2f", "#8e8f94"] };

export function agentTint(id: string): Tint {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return TINTS[h % TINTS.length]!;
}

export function agentInitials(name: string): string {
  const words = name.trim().split(/[\s_-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

const SIZE: Record<number, { radius: string; font: string }> = {
  14: { radius: "rounded-[4px]", font: "text-[6.5px]" },
  16: { radius: "rounded-[4px]", font: "text-[7.5px]" },
  18: { radius: "rounded-[5px]", font: "text-[8px]" },
  20: { radius: "rounded-[6px]", font: "text-[9px]" },
  24: { radius: "rounded-[6px]", font: "text-[10px]" },
  28: { radius: "rounded-[7px]", font: "text-[11px]" },
};

export type AgentPresenceState = "running" | "needs-you" | null;

export function AgentAvatar({
  agent,
  size = 20,
  presence = null,
  neutral = false,
  /** Tailwind border colour class for the presence dot cut-out / stack ring (matches the parent background). */
  ringClassName = "border-sidebar",
  className,
  title,
}: {
  agent: { id: string; name: string; avatarUrl?: string | null };
  size?: 14 | 16 | 18 | 20 | 24 | 28;
  presence?: AgentPresenceState;
  neutral?: boolean;
  ringClassName?: string;
  className?: string;
  title?: string;
}) {
  const tint = neutral ? NEUTRAL_TINT : agentTint(agent.id);
  const s = SIZE[size] ?? SIZE[20]!;
  return (
    <span
      className={cn("relative inline-flex flex-none items-center justify-center overflow-visible", s.radius, className)}
      style={{ width: size, height: size }}
      title={title ?? agent.name}
      aria-hidden={title ? undefined : true}
    >
      {agent.avatarUrl ? (
        <img
          src={agent.avatarUrl}
          alt=""
          className={cn("h-full w-full object-cover", s.radius)}
          style={{ width: size, height: size }}
        />
      ) : (
        <>
          <span
            className={cn("absolute inset-0 dark:hidden", s.radius)}
            style={{ backgroundColor: tint.light[0] }}
          />
          <span
            className={cn("absolute inset-0 hidden dark:block", s.radius)}
            style={{ backgroundColor: tint.dark[0] }}
          />
          <span
            className={cn("relative font-bold leading-none tracking-[0.01em] dark:hidden", s.font)}
            style={{ color: tint.light[1] }}
          >
            {agentInitials(agent.name)}
          </span>
          <span
            className={cn("relative hidden font-bold leading-none tracking-[0.01em] dark:inline", s.font)}
            style={{ color: tint.dark[1] }}
          >
            {agentInitials(agent.name)}
          </span>
        </>
      )}
      {presence && (
        <span
          className={cn(
            "absolute -bottom-0.5 -right-0.5 h-[7px] w-[7px] rounded-full border-[1.5px]",
            ringClassName,
            presence === "running" ? "bg-[#10a37f]" : "bg-[#d97706] dark:bg-[#f59e0b]",
          )}
        />
      )}
    </span>
  );
}

/** Overlapping row of small avatars for the insights "Agents" column. */
export function AgentAvatarStack({
  agents,
  size = 18,
  max = 3,
  ringClassName = "border-background",
}: {
  agents: Array<{ id: string; name: string; avatarUrl?: string | null }>;
  size?: 16 | 18 | 20;
  max?: number;
  ringClassName?: string;
}) {
  const shown = agents.slice(0, max);
  const extra = agents.length - shown.length;
  return (
    <span className="inline-flex items-center">
      {shown.map((a, i) => (
        <span
          key={a.id}
          className={cn("inline-flex rounded-[5px] border-[1.5px]", ringClassName, i > 0 && "-ml-[5px]")}
        >
          <AgentAvatar agent={a} size={size} title={a.name} />
        </span>
      ))}
      {extra > 0 && (
        <span className="ml-1 text-[10.5px] tabular-nums text-text-muted">+{extra}</span>
      )}
    </span>
  );
}
